/**
 * PropertyPWAAdmin — host-side management for the property PWA installation
 * banner + Web Push notifications sent to ex-guests.
 *
 * Two sections stacked vertically:
 *   1. **App installabile (PWA)** — pick a custom square icon (any size, we
 *      resize to 512×512 + 192×192 server-side), pick a display name and a
 *      theme colour. Leaving blanks falls back to Hostland defaults.
 *   2. **Notifiche push** — subscribers count, 4 templates (Promozione,
 *      Sconto ritorno, Nuovo servizio, Aggiornamento), free-form title +
 *      message + optional image + optional link, live phone preview,
 *      "Invia a N ospiti" button. History of last 10 notifications sent.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Save, ArrowLeft, Image as ImageIcon, Bell, Send, Sparkles, Gift,
  Rocket, Info, Upload, ExternalLink, Users, ChevronRight, Loader2, HelpCircle,
  X as XIcon, Smartphone,
} from "lucide-react";
import { API, useAuth } from "../App";
import FeatureTour from "../components/FeatureTour";
import SquareCropModal from "../components/SquareCropModal";
import { PWA_PUSH_TOUR_STEPS } from "../data/tourSteps";

const TEMPLATES = [
  { id: "promo", label: "Promozione stagionale", icon: Sparkles, color: "#FF385C",
    title: "🎁 Offerta speciale per te", message: "Sconto esclusivo sul tuo prossimo soggiorno. Prenota entro fine mese e risparmia il 15%." },
  { id: "return", label: "Sconto ritorno", icon: Gift, color: "#F59E0B",
    title: "Ti aspettiamo di nuovo!", message: "Grazie per essere stato con noi. Ecco un codice sconto -10% per il tuo prossimo soggiorno." },
  { id: "new_service", label: "Nuovo servizio", icon: Rocket, color: "#10B981",
    title: "🆕 Novità nella tua casa vacanza", message: "Abbiamo aggiunto un nuovo servizio: ora è disponibile anche il parcheggio privato." },
  { id: "update", label: "Aggiornamento", icon: Info, color: "#3B82F6",
    title: "📣 Aggiornamento importante", message: "Vogliamo tenerti aggiornato: ecco cosa è cambiato dalla tua ultima visita." },
];

export default function PropertyPWAAdmin() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [propertyName, setPropertyName] = useState("");
  const [config, setConfig] = useState({ name: "", short_name: "", theme_color: "#FF385C", background_color: "#FFFFFF", icon_192: "", icon_512: "" });
  const [subCount, setSubCount] = useState({ active: 0, total: 0, installs: 0, sent: 0, delivered: 0 });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pushImgUploading, setPushImgUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const fileRef = useRef(null);
  const pushImgRef = useRef(null);

  // Notification form
  //   image_url + image_key are populated by /push/image (transient upload
  //   deleted after send). The user can also paste an external image URL —
  //   in that case image_key stays null and no GC happens.
  const [notif, setNotif] = useState({ title: "", message: "", url: "", image_url: "", image_key: null, image_size_kb: null });
  const [tourOpen, setTourOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pwa, subs, hist] = await Promise.all([
        axios.get(`${API}/admin/property/${propertyId}/pwa`, { headers: authHeaders }),
        axios.get(`${API}/admin/property/${propertyId}/push/subscribers`, { headers: authHeaders }),
        axios.get(`${API}/admin/property/${propertyId}/push/history?limit=10`, { headers: authHeaders }),
      ]);
      setPropertyName(pwa.data.property_name || "");
      setConfig({
        name: pwa.data.config?.name || "",
        short_name: pwa.data.config?.short_name || "",
        theme_color: pwa.data.config?.theme_color || "#FF385C",
        background_color: pwa.data.config?.background_color || "#FFFFFF",
        icon_192: pwa.data.config?.icon_192 || "",
        icon_512: pwa.data.config?.icon_512 || "",
      });
      setSubCount(subs.data);
      setHistory(hist.data.items || []);
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (propertyId && token) load(); }, [propertyId, token]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/property/${propertyId}/pwa`, {
        name: config.name || null,
        short_name: config.short_name || null,
        theme_color: config.theme_color,
        background_color: config.background_color,
      }, { headers: authHeaders });
      toast.success("Impostazioni PWA salvate!");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const uploadIcon = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File troppo grande (max 5MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/property/${propertyId}/pwa/icon`, fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      setConfig((c) => ({ ...c, icon_192: r.data.icon_192, icon_512: r.data.icon_512 }));
      toast.success("Icona caricata e ottimizzata!");
    } catch (e) {
      toast.error(`Upload fallito: ${e.response?.data?.detail || e.message}`);
    } finally { setUploading(false); }
  };

  // L'host sceglie un file → apriamo l'EDITOR di ritaglio quadrato (react-easy-crop):
  // può zoomare/spostare per inquadrare bene il logo. Alla conferma carichiamo
  // il PNG 512×512 già quadrato.
  const onPickIcon = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) { toast.error("Usa PNG, JPG o WEBP"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("File troppo grande (max 5MB)"); return; }
    setCropSrc(URL.createObjectURL(f));
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleCropped = async (blob) => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    await uploadIcon(new File([blob], "appicon.png", { type: "image/png" }));
  };

  const applyTemplate = (tpl) => {
    setNotif({ title: tpl.title, message: tpl.message, url: notif.url, image_url: notif.image_url });
  };

  const uploadPushImage = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Immagine troppo grande (max 8MB)"); return; }
    setPushImgUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/property/${propertyId}/push/image`, fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      // Preview uses same-origin CDN URL so browser loads immediately.
      const absUrl = r.data.url.startsWith("http") ? r.data.url : `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      setNotif((n) => ({ ...n, image_url: absUrl, image_key: r.data.key, image_size_kb: r.data.size_kb }));
      toast.success(`Immagine ottimizzata (${r.data.size_kb} KB)`);
    } catch (e) {
      toast.error(`Upload fallito: ${e.response?.data?.detail || e.message}`);
    } finally { setPushImgUploading(false); }
  };

  const clearPushImage = () => {
    setNotif((n) => ({ ...n, image_url: "", image_key: null, image_size_kb: null }));
    if (pushImgRef.current) pushImgRef.current.value = "";
  };

  const sendPush = async () => {
    if (!notif.title.trim() || !notif.message.trim()) { toast.error("Titolo e messaggio obbligatori"); return; }
    const target = subCount.installs || 0;
    if (!window.confirm(target ? `Confermi l'invio della notifica a ${target} device?` : "Confermi l'invio della notifica?")) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/property/${propertyId}/push/send`, {
        title: notif.title.trim(),
        message: notif.message.trim(),
        url: notif.url.trim() || null,
        // Prefer image_key (transient uploaded) so backend can GC after send.
        // If null, fall back to a manually-typed image_url.
        image_url: notif.image_key ? null : (notif.image_url.trim() || null),
        image_key: notif.image_key || null,
      }, { headers: authHeaders });
      toast.success(`Notifica inviata · consegnata a ${r.data.recipients} device!`);
      setNotif({ title: "", message: "", url: "", image_url: "", image_key: null, image_size_kb: null });
      if (pushImgRef.current) pushImgRef.current.value = "";
      load();
    } catch (e) {
      toast.error(`Invio fallito: ${e.response?.data?.detail || e.message}`);
    } finally { setSending(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  const iconPreview = config.icon_192
    ? (config.icon_192.startsWith("http") ? config.icon_192 : `${process.env.REACT_APP_BACKEND_URL}${config.icon_192}`)
    : "/hostland-logo.png";
  const displayName = config.name || propertyName || "Hostland";

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* First-visit walkthrough + on-demand replay via the "Come funziona?" button */}
      <FeatureTour
        storageKey="hostland_tour_pwa_push_v1"
        steps={PWA_PUSH_TOUR_STEPS}
        forceOpen={tourOpen}
        onClose={() => setTourOpen(false)}
      />

      {/* Editor ritaglio quadrato icona app */}
      {cropSrc && (
        <SquareCropModal
          imageSrc={cropSrc}
          onCancel={() => { URL.revokeObjectURL(cropSrc); setCropSrc(null); }}
          onConfirm={handleCropped}
        />
      )}

      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-zinc-100" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-zinc-500 font-bold">App & Notifiche</p>
            <p className="text-sm font-black text-zinc-900 truncate">{propertyName}</p>
          </div>
          <button
            type="button"
            onClick={() => setTourOpen(true)}
            className="inline-flex items-center gap-1.5 text-[11px] font-black text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-full px-3 py-1.5 transition-colors"
            data-testid="pwa-tour-btn"
          >
            <HelpCircle className="w-3.5 h-3.5" /> Come funziona?
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-4 space-y-6">

        {/* ═══ SECTION 1: PWA CONFIG ═══ */}
        <section className="bg-white rounded-3xl border border-zinc-100 p-5 md:p-6" data-testid="pwa-section">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#FF385C] to-[#E61E4D] flex items-center justify-center shrink-0">
              <ImageIcon className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <h2 className="text-lg font-black text-zinc-900">App installabile</h2>
              <p className="text-xs text-zinc-500 leading-snug">Quando l'ospite apre la tua landing su cellulare, gli proponiamo di installare la web-app. Personalizza qui l'icona e il nome che vedrà sulla schermata Home.</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <p className="text-[11px] font-black uppercase tracking-widest text-zinc-600 mb-2">Icona app</p>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-2xl overflow-hidden bg-zinc-100 shadow-sm shrink-0">
                  <img src={iconPreview} alt="Icona PWA" className="w-full h-full object-cover" data-testid="pwa-icon-preview" />
                </div>
                <div className="flex-1 min-w-0">
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                    onChange={onPickIcon} data-testid="pwa-icon-input" />
                  <button onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-zinc-900 text-white text-sm font-bold disabled:opacity-60"
                    data-testid="pwa-icon-upload-btn">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={2.4} />}
                    {uploading ? "Ottimizzo..." : "Carica icona"}
                  </button>
                  <p className="text-[11px] text-zinc-500 mt-2 leading-snug">
                    Carica qualsiasi immagine (PNG/JPG/WebP, max 5MB): si apre un <b>editor</b> dove la ritagli <b>quadrata</b> con zoom e trascinamento. Se non carichi nulla uso l'icona Hostland.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-zinc-600 mb-1 block">Nome app (fino a 45 caratteri)</label>
                <input type="text" value={config.name} maxLength={45}
                  onChange={(e) => setConfig((c) => ({ ...c, name: e.target.value }))}
                  placeholder={propertyName || "Casa Meizon Genova"}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-zinc-200 text-sm bg-white"
                  data-testid="pwa-name-input" />
              </div>
              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-zinc-600 mb-1 block">Nome corto (max 12 caratteri, mostrato sotto l'icona su iOS)</label>
                <input type="text" value={config.short_name} maxLength={12}
                  onChange={(e) => setConfig((c) => ({ ...c, short_name: e.target.value }))}
                  placeholder="Casa Meizon"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-zinc-200 text-sm bg-white"
                  data-testid="pwa-short-input" />
              </div>
              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-zinc-600 mb-1 block">Colore tema</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.theme_color}
                    onChange={(e) => setConfig((c) => ({ ...c, theme_color: e.target.value }))}
                    className="w-12 h-10 rounded-xl border border-zinc-200 cursor-pointer"
                    data-testid="pwa-theme-color" />
                  <input type="text" value={config.theme_color} readOnly
                    className="flex-1 px-3.5 py-2.5 rounded-xl border border-zinc-200 text-sm bg-zinc-50 font-mono" />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2 items-center justify-between">
            <p className="text-xs text-zinc-500 inline-flex items-center gap-1.5">
              <ExternalLink className="w-3 h-3" />
              Preview live: <a href={`/property/${propertyId}`} target="_blank" rel="noopener noreferrer" className="font-bold text-zinc-900 hover:underline">apri la landing</a>
            </p>
            <button onClick={saveConfig} disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#FF385C] text-white text-sm font-black shadow-lg shadow-rose-500/25 disabled:opacity-60"
              data-testid="pwa-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={2.6} />}
              Salva impostazioni
            </button>
          </div>
        </section>

        {/* ═══ SECTION 2: PUSH NOTIFICATIONS ═══ */}
        <section className="bg-white rounded-3xl border border-zinc-100 p-5 md:p-6" data-testid="push-section">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shrink-0">
              <Bell className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-black text-zinc-900">Notifiche push</h2>
              <p className="text-xs text-zinc-500 leading-snug">Manda promozioni e aggiornamenti agli ex-ospiti che hanno installato la tua app.</p>
            </div>
          </div>

          {/* ─── Stat: un unico contatore DEVICE + notifiche inviate/consegnate ─── */}
          <div className="mb-5 grid grid-cols-2 gap-3" data-testid="push-audience-card">
            <div className="rounded-2xl bg-gradient-to-br from-violet-50 to-fuchsia-100/40 border border-violet-200/60 p-4" data-testid="install-stat-card">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center shadow-sm shrink-0">
                  <Smartphone className="w-5 h-5 text-violet-700" strokeWidth={2.2} />
                </div>
                <p className="text-[10px] font-black uppercase tracking-widest text-violet-700/70 leading-tight">App installata</p>
              </div>
              <p className="text-3xl font-black text-violet-900 leading-none" data-testid="install-count">
                {subCount.installs}
                <span className="text-sm font-bold text-violet-700/70 ml-1.5">device</span>
              </p>
              <p className="text-[11px] text-violet-800/70 mt-1 leading-snug">Dispositivi che hanno aggiunto l'app alla Home. Ricevono le tue notifiche.</p>
            </div>

            <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100/40 border border-emerald-200/60 p-4" data-testid="notif-stat-card">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center shadow-sm shrink-0">
                  <Bell className="w-5 h-5 text-emerald-700" strokeWidth={2.2} />
                </div>
                <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700/70 leading-tight">Notifiche</p>
              </div>
              <div className="flex items-end gap-5">
                <div>
                  <p className="text-3xl font-black text-emerald-900 leading-none" data-testid="push-sent-count">{subCount.sent}</p>
                  <p className="text-[11px] font-bold text-emerald-800/70 mt-1">Inviate</p>
                </div>
                <div>
                  <p className="text-3xl font-black text-emerald-900 leading-none" data-testid="push-delivered-count">{subCount.delivered}</p>
                  <p className="text-[11px] font-bold text-emerald-800/70 mt-1">Consegnate</p>
                </div>
              </div>
            </div>
          </div>

          {/* Templates */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            {TEMPLATES.map((tpl) => (
              <button key={tpl.id} onClick={() => applyTemplate(tpl)}
                className="flex items-center gap-2 p-3 rounded-2xl border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50 text-left transition-colors"
                data-testid={`push-tpl-${tpl.id}`}>
                <div className="shrink-0 w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${tpl.color}18`, color: tpl.color }}>
                  <tpl.icon className="w-4 h-4" strokeWidth={2.4} />
                </div>
                <span className="text-[11px] font-black text-zinc-800 leading-tight">{tpl.label}</span>
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-zinc-600 mb-1 block">Titolo *</label>
                <input type="text" value={notif.title} maxLength={120}
                  onChange={(e) => setNotif((n) => ({ ...n, title: e.target.value }))}
                  placeholder="🎁 Offerta speciale per te"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-zinc-200 text-sm"
                  data-testid="push-title-input" />
              </div>
              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-zinc-600 mb-1 block">Messaggio *</label>
                <textarea value={notif.message} maxLength={1000} rows={3}
                  onChange={(e) => setNotif((n) => ({ ...n, message: e.target.value }))}
                  placeholder="Sconto -15% sul tuo prossimo soggiorno se prenoti entro fine mese."
                  className="w-full px-3.5 py-2.5 rounded-xl border border-zinc-200 text-sm resize-none"
                  data-testid="push-message-input" />
              </div>
              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-zinc-600 mb-1 block">Link (facoltativo)</label>
                <input type="url" value={notif.url}
                  onChange={(e) => setNotif((n) => ({ ...n, url: e.target.value }))}
                  placeholder="https://hostland.vitadahost.it/property/..."
                  className="w-full px-3.5 py-2.5 rounded-xl border border-zinc-200 text-sm font-mono"
                  data-testid="push-url-input" />
              </div>
              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-zinc-600 mb-1 block">
                  Immagine copertina (facoltativa)
                </label>
                {notif.image_url ? (
                  <div className="relative rounded-xl overflow-hidden border border-zinc-200 bg-zinc-50" data-testid="push-image-preview">
                    <img src={notif.image_url} alt="Anteprima" className="w-full max-h-40 object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    <button
                      type="button"
                      onClick={clearPushImage}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
                      data-testid="push-image-clear"
                      aria-label="Rimuovi immagine"
                    >
                      <XIcon className="w-4 h-4" strokeWidth={2.4} />
                    </button>
                    {notif.image_size_kb != null && (
                      <span className="absolute bottom-2 left-2 text-[10px] font-black uppercase tracking-widest bg-black/60 text-white px-2 py-1 rounded-full">
                        Ottimizzata · {notif.image_size_kb} KB
                      </span>
                    )}
                  </div>
                ) : (
                  <>
                    <input
                      ref={pushImgRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => uploadPushImage(e.target.files?.[0])}
                      data-testid="push-image-input"
                    />
                    <button
                      type="button"
                      onClick={() => pushImgRef.current?.click()}
                      disabled={pushImgUploading}
                      className="w-full inline-flex items-center justify-center gap-2 px-3.5 py-3 rounded-xl border-2 border-dashed border-zinc-300 hover:border-zinc-400 text-sm font-bold text-zinc-600 hover:bg-zinc-50 transition-colors disabled:opacity-60"
                      data-testid="push-image-upload-btn"
                    >
                      {pushImgUploading
                        ? (<><Loader2 className="w-4 h-4 animate-spin" /> Ottimizzo…</>)
                        : (<><ImageIcon className="w-4 h-4" strokeWidth={2.4} /> Carica immagine</>)}
                    </button>
                    <p className="text-[10.5px] text-zinc-400 mt-1.5 leading-snug">
                      La ottimizzo io fino a ~250 KB (max 1080 px). Dopo l'invio la elimino automaticamente per non occupare spazio.
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Phone preview */}
            <div className="flex flex-col items-center">
              <p className="text-[11px] font-black uppercase tracking-widest text-zinc-500 mb-3">Anteprima notifica</p>
              <div className="w-full max-w-[280px] bg-zinc-100 rounded-3xl p-3 border border-zinc-200">
                <div className="bg-white rounded-2xl p-3 shadow-sm">
                  <div className="flex items-start gap-2.5">
                    <img src={iconPreview} alt="" className="w-8 h-8 rounded-lg object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">{displayName}</p>
                      <p className="text-[13px] font-black text-zinc-900 leading-tight mt-0.5 line-clamp-2">
                        {notif.title || "Titolo notifica"}
                      </p>
                      <p className="text-[11px] text-zinc-600 leading-tight mt-1 line-clamp-3">
                        {notif.message || "Ecco come apparirà il messaggio ai tuoi ospiti sul loro telefono."}
                      </p>
                    </div>
                  </div>
                  {notif.image_url && (
                    <img src={notif.image_url} alt="" className="w-full h-24 object-cover rounded-lg mt-2" onError={(e) => e.currentTarget.style.display = "none"} />
                  )}
                </div>
                <p className="text-center text-[9px] text-zinc-400 mt-2">via Web Push · OneSignal</p>
              </div>

              <button onClick={sendPush} disabled={sending}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-[#FF385C] text-white font-black text-sm shadow-xl shadow-rose-500/30 disabled:opacity-60 disabled:shadow-none"
                data-testid="push-send-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={2.4} />}
                {sending ? "Invio in corso…" : "Invia notifica"}
              </button>
            </div>
          </div>
        </section>

        {/* ═══ HISTORY ═══ */}
        {history.length > 0 && (
          <section className="bg-white rounded-3xl border border-zinc-100 p-5 md:p-6" data-testid="push-history">
            <h3 className="text-sm font-black text-zinc-900 mb-3">Ultime notifiche inviate</h3>
            <ul className="divide-y divide-zinc-100">
              {history.map((h, i) => (
                <li key={i} className="py-3 flex items-start gap-3">
                  <ChevronRight className="w-4 h-4 text-zinc-300 mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black text-zinc-900 truncate">{h.title}</p>
                    <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{h.message}</p>
                    <p className="text-[10px] text-zinc-400 mt-1 font-bold uppercase tracking-wider">
                      {new Date(h.sent_at).toLocaleString("it-IT")} · {h.recipients} destinatari
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
